"use client";

/**
 * WebSocket Manager
 *
 * Singleton manager for WebSocket connections to the Go backend.
 * Features:
 * - Topic-based subscriptions with deduplication
 * - Automatic reconnection with exponential backoff
 * - Token refresh handling
 * - Connection state management
 */

// Debug flag for development logging
const DEBUG_WS = process.env.NODE_ENV === "development";

// Message types (matches Go backend)
export type WSMessageType =
  | "subscribe"
  | "unsubscribe"
  | "subscribed"
  | "success"
  | "telemetry"
  | "twin_update"
  | "alarm"
  | "notification"
  | "unread_count"
  | "ping"
  | "pong"
  | "heartbeat"
  | "error"
  | "endpoint_update"
  | "rulechain_debug"
  | "rulechain_input"
  | "trigger_injection"
  | "trigger_injection_ack";

// Message structure
export interface WSMessage {
  type: WSMessageType;
  topic?: string;
  topics?: string[];
  deviceId?: string;
  data?: Record<string, unknown>;
  initialState?: Record<string, unknown>;
  code?: string;
  error?: string;
}

// Telemetry value format
export interface TelemetryValue {
  value: unknown;
  ts: number;
}

// Handler callback type
export type MessageHandler = (message: WSMessage) => void;

// Connection state callback type
export type ConnectionStateHandler = (isConnected: boolean) => void;

// WebSocket connection states
export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

/**
 * WebSocket Manager Class
 */
class WebSocketManager {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, Set<MessageHandler>> = new Map();
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timeout | null = null;
  private currentToken: string | null = null;
  private connectionState: ConnectionState = "disconnected";
  private pendingSubscriptions: string[] = [];

  /**
   * Get the WebSocket URL based on current location
   */
  private getWebSocketUrl(token: string): string {
    if (typeof window === "undefined") {
      return "";
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/api/v1/ws?token=${token}`;
  }

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (typeof window === "undefined") {
      return;
    }

    // If already connected with same token, skip
    if (this.ws?.readyState === WebSocket.OPEN && this.currentToken === token) {
      return;
    }

    // Close existing connection
    this.disconnect(false);

    this.currentToken = token;
    this.connectionState = "connecting";
    this.notifyConnectionState(false);

    const wsUrl = this.getWebSocketUrl(token);

    if (DEBUG_WS) {
      console.log(
        "🔌 [WebSocket] Connecting to",
        wsUrl.replace(/token=.*/, "token=***")
      );
    }

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (DEBUG_WS) {
          console.log("✅ [WebSocket] Connected");
        }

        this.reconnectAttempts = 0;
        this.connectionState = "connected";
        this.notifyConnectionState(true);

        // Resubscribe to all existing subscriptions
        this.resubscribeAll();

        // Start ping timer
        this.startPingTimer();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error("❌ [WebSocket] Failed to parse message:", error);
        }
      };

      this.ws.onclose = (event) => {
        if (DEBUG_WS) {
          console.log("🔌 [WebSocket] Disconnected:", event.code, event.reason);
        }

        this.stopPingTimer();
        this.connectionState = "disconnected";
        this.notifyConnectionState(false);

        // Attempt reconnection if not intentional disconnect
        if (this.currentToken && event.code !== 1000) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        // Use console.warn instead of console.error for WebSocket errors
        // to prevent triggering error overlays in mobile WebView
        // The error is usually a disconnection which will be handled by onclose
        console.warn("[WebSocket] Connection error (will attempt reconnect):", error);
      };
    } catch (error) {
      console.error("❌ [WebSocket] Failed to create connection:", error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(clearSubscriptions = true): void {
    this.stopPingTimer();
    this.stopReconnectTimer();

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnection
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    if (clearSubscriptions) {
      this.currentToken = null;
      this.subscriptions.clear();
      this.pendingSubscriptions = [];
    }

    this.connectionState = "disconnected";
    this.notifyConnectionState(false);

    if (DEBUG_WS) {
      console.log("🔌 [WebSocket] Disconnected by client");
    }
  }

  /**
   * Subscribe to a topic
   * Returns unsubscribe function
   */
  subscribe(topic: string, handler: MessageHandler): () => void {
    // Add handler to subscription map
    let handlers = this.subscriptions.get(topic);
    const isNewSubscription = !handlers;

    if (!handlers) {
      handlers = new Set();
      this.subscriptions.set(topic, handlers);
    }
    handlers.add(handler);

    if (DEBUG_WS) {
      console.log(
        `📡 [WebSocket] Subscribed to "${topic}" (${handlers.size} handlers)`
      );
    }

    // If new subscription, send subscribe message to server
    if (isNewSubscription) {
      this.sendSubscribe([topic]);
    }

    // Return unsubscribe function
    return () => this.unsubscribe(topic, handler);
  }

  /**
   * Unsubscribe a handler from a topic
   */
  private unsubscribe(topic: string, handler: MessageHandler): void {
    const handlers = this.subscriptions.get(topic);
    if (!handlers) {
      return;
    }

    handlers.delete(handler);

    if (DEBUG_WS) {
      console.log(
        `📡 [WebSocket] Unsubscribed from "${topic}" (${handlers.size} handlers remaining)`
      );
    }

    // If no more handlers, remove subscription entirely
    if (handlers.size === 0) {
      this.subscriptions.delete(topic);
      this.sendUnsubscribe([topic]);
    }
  }

  /**
   * Register a connection state handler
   */
  onConnectionChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);

    // Immediately notify current state
    handler(this.connectionState === "connected");

    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  /**
   * Get current connection state
   */
  get isConnected(): boolean {
    return this.connectionState === "connected";
  }

  /**
   * Get current connection state string
   */
  get state(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(message: WSMessage): void {
    if (DEBUG_WS && message.type !== "pong" && message.type !== "heartbeat") {
      console.log("📨 [WebSocket] Message:", message.type, message.topic);
    }

    switch (message.type) {
      case "subscribed":
        // Subscription confirmed with initial state
        this.notifyHandlers(message.topic!, message);
        break;

      case "telemetry":
        // Telemetry update for a device
        this.notifyHandlers(message.topic!, message);
        // Also notify base device topic
        if (message.deviceId) {
          this.notifyHandlers(`device:${message.deviceId}`, message);
        }
        break;

      case "twin_update":
        // Device twin update
        this.notifyHandlers(message.topic!, message);
        // Also notify base device topic
        if (message.deviceId) {
          this.notifyHandlers(`device:${message.deviceId}`, message);
        }
        break;

      case "alarm":
        // Alarm notification
        this.notifyHandlers(message.topic!, message);
        this.notifyHandlers("alarms:active", message);
        break;

      case "notification":
        // In-app notification
        this.notifyHandlers(message.topic!, message);
        break;

      case "unread_count":
        // Unread notification count update
        this.notifyHandlers(message.topic!, message);
        break;

      case "endpoint_update":
        // Endpoint subscription update (real-time list updates)
        this.notifyHandlers(message.topic!, message);
        break;

      case "rulechain_debug":
        // Rule chain debug message from live telemetry processing
        this.notifyHandlers(message.topic!, message);
        break;

      case "rulechain_input":
        // Original input telemetry that triggered a rule chain execution
        this.notifyHandlers(message.topic!, message);
        break;

      case "trigger_injection_ack":
        // Acknowledgment of manual injection trigger
        // Notify handlers that might be waiting for the result
        if (message.data?.rule_chain_id) {
          const topic = `rulechain:${message.data.rule_chain_id}:debug`;
          this.notifyHandlers(topic, message);
        }
        if (DEBUG_WS) {
          console.log("📨 [WebSocket] Injection triggered:", message.data);
        }
        break;

      case "pong":
      case "heartbeat":
      case "success":
        // Keep-alive response / ack, nothing to do
        break;

      case "error":
        console.error(
          "❌ [WebSocket] Server error:",
          message.code,
          message.error
        );
        break;

      default:
        if (DEBUG_WS) {
          console.log("📨 [WebSocket] Unknown message type:", message.type);
        }
    }
  }

  /**
   * Notify all handlers for a topic
   */
  private notifyHandlers(topic: string, message: WSMessage): void {
    const handlers = this.subscriptions.get(topic);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("❌ [WebSocket] Handler error:", error);
        }
      });
    }
  }

  /**
   * Notify connection state handlers
   */
  private notifyConnectionState(isConnected: boolean): void {
    this.connectionStateHandlers.forEach((handler) => {
      try {
        handler(isConnected);
      } catch (error) {
        console.error("❌ [WebSocket] Connection state handler error:", error);
      }
    });
  }

  /**
   * Send subscribe message to server
   */
  private sendSubscribe(topics: string[]): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message: WSMessage = {
        type: "subscribe",
        topics,
      };
      this.ws.send(JSON.stringify(message));

      if (DEBUG_WS) {
        console.log("📤 [WebSocket] Sent subscribe:", topics);
      }
    } else {
      // Store pending subscriptions for when connection is ready
      this.pendingSubscriptions.push(...topics);
    }
  }

  /**
   * Send unsubscribe message to server
   */
  private sendUnsubscribe(topics: string[]): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message: WSMessage = {
        type: "unsubscribe",
        topics,
      };
      this.ws.send(JSON.stringify(message));

      if (DEBUG_WS) {
        console.log("📤 [WebSocket] Sent unsubscribe:", topics);
      }
    }
  }

  /**
   * Resubscribe to all current subscriptions after reconnection
   */
  private resubscribeAll(): void {
    // Get all current subscription topics
    const topics = Array.from(this.subscriptions.keys());

    // Include any pending subscriptions
    const allTopics = [...new Set([...topics, ...this.pendingSubscriptions])];
    this.pendingSubscriptions = [];

    if (allTopics.length > 0) {
      this.sendSubscribe(allTopics);

      if (DEBUG_WS) {
        console.log(
          "📤 [WebSocket] Resubscribed to",
          allTopics.length,
          "topics"
        );
      }
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("❌ [WebSocket] Max reconnection attempts reached");
      return;
    }

    this.connectionState = "reconnecting";

    const delay = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    if (DEBUG_WS) {
      console.log(
        `🔄 [WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
      );
    }

    this.reconnectTimer = setTimeout(() => {
      if (this.currentToken) {
        this.connect(this.currentToken);
      }
    }, delay);
  }

  /**
   * Stop reconnection timer
   */
  private stopReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Start ping timer for keep-alive
   */
  private startPingTimer(): void {
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping timer
   */
  private stopPingTimer(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * Update token (for token refresh)
   */
  updateToken(token: string): void {
    if (this.currentToken !== token) {
      this.currentToken = token;
      // Reconnect with new token
      if (this.isConnected) {
        this.disconnect(false);
        this.connect(token);
      }
    }
  }

  /**
   * Send a message to the WebSocket server
   * Returns true if the message was sent, false if not connected
   */
  send(message: WSMessage): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      if (DEBUG_WS) {
        console.log("📤 [WebSocket] Sent message:", message.type, message.data);
      }
      return true;
    }
    if (DEBUG_WS) {
      console.warn("⚠️ [WebSocket] Cannot send message, not connected");
    }
    return false;
  }
}

// Export singleton instance
export const wsManager = new WebSocketManager();
